/**
 * 10.4 value bridge (scoring.scoreValueCreated@2.0): where Value Created vs the status-quo twin came from.
 * Sequential substitution in `ValueBridgeStep.step` order: the terminal value's inputs x₁…x_K move from the twin's to the team's one at a time,
 * step_k = [W(x₁…x_k team, rest twin) − W(x₁…x_{k−1} team, rest twin)] ÷ W_auto, and `interaction` = VC − Σ step_k (0 up to rounding and the clamps; with the 2/3–1/3 dial
 * on, the market price's own difference lands here). W re-evaluates the terminal value (10.3 E2, E5) with the 10.2 E3 standing cost of equity. The inputs, as one look-through
 * result carries them: ROTCE_LT, g_LT, TBV_req, {Excess, Shortfall, CET1, market value, share factor, distributions PV_T}, Marks and each standing π_k; event premia are not in the
 * terminal value, so they make no step.
 * The look-through give-back (ADR-0042) is the gap between the terminal value and the market price at the round end: its steps are the team's 10.3 E6 lines minus the twin's.
 */
import { CAPITAL_RECORD_COUNTS, PREMIUM_KINDS, type Bp, TERMINAL_LINES, type ContentParams, type DecisionId, type LookThroughResult, type Rate, type SeatRoundResult, type UsdPerShare, type ValueBridgeStep } from "@bsim/domain";
import { coeOf, PREMIUM_ROWS, type PremiaBp } from "./cost-of-equity";
import { terminalTermsOf, terminalValue } from "./terminal";
import { scoreValueCreated } from "./value-created";

type Premium = (typeof PREMIUM_ROWS)[number];
type Params = ContentParams;

/** The terminal inputs a step substitutes; shareFactor = V_T per share ÷ V_T (1 ÷ sh_T), D = compounded distributions. */
interface Inputs {
  readonly rotceRate: number; readonly growthRate: number; readonly tbvRequiredUsd: number; readonly cet1Usd: number; readonly marketValueUsd: number;
  readonly marksUsd: number; readonly beyondCapUsd: number; readonly premia: PremiaBp; readonly shareFactor: number; readonly marketPriceUsdPerShare: number; readonly distributionsUsdPerShare: number;
  /** The capital record K behind the capital premium, where the valuation carries it (for the premium's cause line only). */
  readonly capitalRecordRate?: number | undefined;
}

/** Decisions that move each premium's input (ValueBridgeStep contract). */
const PREMIUM_DECISIONS: Readonly<Record<Premium, readonly DecisionId[]>> = {
  capital: ["capital.dividend", "capital.buyback", "capital.issuance"],
  creConcentration: ["loans.cre.growth", "loans.cre.underwriting"],
  wholesale: ["loans.ci.growth", "loans.cre.growth", "loans.resi.growth", "loans.consumer.growth", "treasury.funding"],
  uninsured: ["deposits.retailChecking.rate", "deposits.retailMm.rate", "deposits.cd.rate", "deposits.commercialChecking.ecr", "deposits.commercialMm.rate"],
  eve: ["treasury.securitiesMix", "treasury.ratePosition"],
  dividend: ["capital.dividend"],
  enforcement: [],
  event: [],
};

/** The terminal weight of the score (10.4 E1). */
const weightOf = (params: Params["valuation"]): number => (params.terminal.dialEnabled ? params.terminal.dialTerminalWeightRate : params.terminal.weightRate);

const termsOf = (x: Inputs, params: Params["valuation"]) => ({
  rotceRate: x.rotceRate, growthRate: x.growthRate, coeStandingRate: coeOf(params, x.premia).standingRate, tbvRequiredUsd: x.tbvRequiredUsd,
  marksUsd: x.marksUsd, cet1Usd: x.cet1Usd, marketValueUsd: x.marketValueUsd, beyondCapUsd: x.beyondCapUsd,
});

const wealthOf = (x: Inputs, params: Params): number => {
  const w = weightOf(params.valuation);
  return (1 - w) * x.marketPriceUsdPerShare + w * x.shareFactor * terminalValue(termsOf(x, params.valuation), params.valuation, params.capitalPolicy.issuance).valueUsd + x.distributionsUsdPerShare;
};

/** The inputs of a scored look-through, or undefined where it ran no terminal value. */
function inputsOf(lt: LookThroughResult, params: Params): Inputs | undefined {
  const { valuation: v, rotceLookThroughRate: rotceRate, growthLookThroughRate: growthRate, sharesOutstanding: shares } = lt;
  if (rotceRate === undefined || growthRate === undefined || shares === undefined) return undefined;
  const { standingPremiaBp, capitalLookThroughRate } = lt; // 10.3 E5: the window the terminal value priced (CP-1 v2.40)
  const t = terminalTermsOf(v, shares, { rotceRate, growthRate }, params.valuation);
  // The terminal prices the window's standing rows, not T's (10.3 E5): the walk substitutes those, so the premium steps and the terminal effect of the window are the walk's, not the residual's.
  const premia: PremiaBp = standingPremiaBp === undefined ? v.premiaBp : { ...v.premiaBp, ...standingPremiaBp };
  const capitalRecordRate = v.capitalRecordRate === undefined ? undefined : Math.min(v.capitalRecordRate, capitalLookThroughRate ?? v.capitalRecordRate);
  return {
    rotceRate, growthRate, tbvRequiredUsd: t.tbvRequiredUsd, cet1Usd: t.cet1Usd, marketValueUsd: t.marketValueUsd, marksUsd: t.marksUsd, beyondCapUsd: (lt.franchiseBeyondCapUsdPerShare ?? 0) * shares,
    premia, shareFactor: 1 / shares, marketPriceUsdPerShare: lt.marketPriceUsdPerShare ?? v.priceUsdPerShare, distributionsUsdPerShare: lt.distributionsCompoundedUsdPerShare, capitalRecordRate,
  };
}

/** What each premium row prices, as a noun after "your" (the premium table's rows, 10.2 E2). */
const PREMIUM_NAME: Readonly<Record<Premium, string>> = {
  capital: "capital record", creConcentration: "CRE concentration", wholesale: "wholesale funding", uninsured: "uninsured deposits", eve: "rate risk",
  dividend: "dividend cut", enforcement: "public enforcement action", event: "event this year",
};

/**
 * ADR-0052: what a premium step is for, with its sign named. The seat's premium against its twin's, at the end of the walk: a seat paying more is charged a surcharge, one paying less is
 * given a release (the market charges it less than its twin), so a positive "surcharge" never appears. The capital row adds the record K behind it and the line it is read against.
 */
function causeOf(premium: Premium, to: Ends, params: Params["valuation"]): string {
  const [you, twin] = [to.team.premia[premium], to.twin.premia[premium]];
  const bp = (x: number): string => `${Math.round(x)}bp`;
  const pct = (x: number): string => `${(x * 100).toFixed(1)}%`;
  const lead = you === twin ? "No difference" : you > twin ? "Surcharge" : "Release";
  const against = `the market adds ${bp(you)} to its required return for your ${PREMIUM_NAME[premium]}, ${bp(twin)} for your twin's`;
  const k = to.team.capitalRecordRate, kTwin = to.twin.capitalRecordRate;
  const capital = premium !== "capital" || k === undefined || kTwin === undefined
    ? "" : `; your capital record is ${pct(k)} against your twin's ${pct(kTwin)} (${CAPITAL_RECORD_COUNTS}, averaged over every quarter-end, or the look-through years' average CET1 if lower), and the market adds ${params.premia.capital.slope1BpPerPp}bp per point under ${pct(params.premia.capital.threshold1Rate)}`;
  return `${lead}: ${against}${capital}.`;
}

interface Substitution { readonly step: ValueBridgeStep["step"]; readonly premium?: Premium; readonly apply: (x: Inputs, to: Inputs) => Inputs }

const SUBSTITUTIONS: readonly Substitution[] = [
  { step: "coreEarnings", apply: (x, t) => ({ ...x, rotceRate: t.rotceRate, beyondCapUsd: t.beyondCapUsd }) },
  { step: "growth", apply: (x, t) => ({ ...x, growthRate: t.growthRate }) },
  { step: "requiredCapitalGrowth", apply: (x, t) => ({ ...x, tbvRequiredUsd: t.tbvRequiredUsd }) },
  // Q2 (E2 item 7): the lumped step split in the oracle walk's order (ch10 `_walk`: distributions, shares retired, then the capital line), so the bridge and the decomposition share one vocabulary.
  // The capital line (excess or shortfall at T, and the market value its dilution is priced on) keeps the member `excessShortfallAndDistributions`: the union has no member of its own for it.
  { step: "distributions", apply: (x, t) => ({ ...x, distributionsUsdPerShare: t.distributionsUsdPerShare }) },
  { step: "sharesRetired", apply: (x, t) => ({ ...x, shareFactor: t.shareFactor }) },
  { step: "excessShortfallAndDistributions", apply: (x, t) => ({ ...x, cet1Usd: t.cet1Usd, marketValueUsd: t.marketValueUsd }) },
  { step: "marks", apply: (x, t) => ({ ...x, marksUsd: t.marksUsd }) },
  ...PREMIUM_ROWS.filter((premium) => PREMIUM_KINDS[premium] === "standing").map((premium): Substitution => ({ step: "coePremium", premium, apply: (x, t) => ({ ...x, premia: { ...x.premia, [premium]: t.premia[premium] } }) })),
];

/** The seat's and the twin's inputs at one end of a walk. */
interface Ends { readonly team: Inputs; readonly twin: Inputs }

/** 10.4: Value Created vs the twin, the twin's terminal inputs moved to the seat's (the twin's side stays where it is). */
export function valueBridge(seat: SeatRoundResult, twin: LookThroughResult, base: UsdPerShare, params: Params): readonly ValueBridgeStep[] {
  const total = scoreValueCreated(seat.lookThrough, twin);
  const [team, auto] = [inputsOf(seat.lookThrough, params), inputsOf(twin, params)];
  if (team === undefined || auto === undefined) return [{ step: "interaction", rate: total, usdPerShare: (total * base) as UsdPerShare, decisionIds: [] }];
  const ratio = (x: Ends): number => wealthOf(x.team, params) / wealthOf(x.twin, params) - 1;
  const to: Ends = { team, twin: auto };
  const steps: ValueBridgeStep[] = [];
  let x: Ends = { team: auto, twin: auto };
  let previous = ratio(x);
  let explained = 0;
  for (const s of SUBSTITUTIONS) {
    x = { team: s.apply(x.team, to.team), twin: s.apply(x.twin, to.twin) };
    const now = ratio(x);
    const rate = now - previous;
    previous = now;
    explained += rate;
    steps.push({ step: s.step, ...(s.premium ? { premium: s.premium } : {}), rate: rate as Rate, usdPerShare: (rate * base) as UsdPerShare, decisionIds: s.premium ? [...PREMIUM_DECISIONS[s.premium]] : [], ...(s.premium ? { cause: causeOf(s.premium, to, params.valuation), premiumBp: { you: to.team.premia[s.premium] as Bp, twin: to.twin.premia[s.premium] as Bp } } : {}) });
  }
  const residual = total - explained;
  steps.push({ step: "interaction", rate: residual as Rate, usdPerShare: (residual * base) as UsdPerShare, decisionIds: [] });
  return steps;
}

/**
 * ADR-0042, 10.3 E6: the look-through give-back, the terminal value against the market price at the round end. The walk starts at reported-only VC and adds the named lines one at a time,
 * the seat's and its twin's together, each weighted by the terminal weight: step_k is the move in W_team ÷ W_auto, so it ends at the official VC and `interaction` is 0 up to rounding
 * (it takes whatever is left when the results carry no lines).
 */
export function lookThroughGiveBack(seat: SeatRoundResult, base: UsdPerShare, params: Params): readonly ValueBridgeStep[] {
  const { lines: team } = seat.lookThrough, { lines: twin } = seat.autopilotLookThrough;
  const w = weightOf(params.valuation);
  const reported = { team: seat.reportedWealthUsdPerShare, twin: seat.valueTwinRoundEnd.reportedWealthUsdPerShare };
  const total = scoreValueCreated(seat.lookThrough, seat.autopilotLookThrough) - (reported.team / reported.twin - 1);
  const steps: ValueBridgeStep[] = [];
  if (team !== undefined && twin !== undefined) {
    const wealth = { team: reported.team as number, twin: reported.twin as number };
    let previous = reported.team / reported.twin;
    for (const line of TERMINAL_LINES) {
      wealth.team += w * (team[line] as number);
      wealth.twin += w * (twin[line] as number);
      const now = wealth.team / wealth.twin;
      steps.push({ step: "terminalLine", line, rate: (now - previous) as Rate, usdPerShare: ((now - previous) * base) as UsdPerShare, decisionIds: [] });
      previous = now;
    }
  }
  const residual = total - steps.reduce((s, x) => s + x.rate, 0);
  steps.push({ step: "interaction", rate: residual as Rate, usdPerShare: (residual * base) as UsdPerShare, decisionIds: [] });
  return steps;
}
